import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mic, Video, Settings, Copy, ChevronDown, User, Shield, MessageSquare } from 'lucide-react';
import Preview from '../../base/premeeting/components/web/Preview';
import { updateSettings } from '../../base/settings/actions';
import { createLocalTracksA } from '../../base/tracks/actions.any';
import { getLocalJitsiVideoTrack } from '../../base/tracks/functions.web';
import { copyText } from '../../base/util/copyText.web';
import { openSettingsDialog } from '../../settings/actions';
import { SETTINGS_TABS } from '../../settings/constants';
export default function HostMeetingView({ onHost, generatedRoomName }) {
    const dispatch = useDispatch();
    const videoTrack = useSelector((state) => getLocalJitsiVideoTrack(state));
    const settings = useSelector((state) => state['features/base/settings']);
    const [name, setName] = useState(settings.displayName || 'Guest');
    const [videoOn, setVideoOn] = useState(true);
    const [micOn, setMicOn] = useState(true);
    const [waitingRoom, setWaitingRoom] = useState(true);
    const [requirePasscode, setRequirePasscode] = useState(false);
    const [liveTranscription, setLiveTranscription] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    useEffect(() => {
        dispatch(createLocalTracksA({ devices: ['video', 'audio'] }));
    }, [dispatch]);
    const handleHost = () => {
        dispatch(updateSettings({ displayName: name }));
        let roomToJoin = generatedRoomName;
        if (!roomToJoin || roomToJoin.trim() === '') {
            // Generate a random room name if empty
            roomToJoin = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
        }
        let passcode = '';
        if (requirePasscode) {
            passcode = Math.floor(100000 + Math.random() * 900000).toString(); // 6 digit passcode
        }
        onHost(roomToJoin, {
            videoOn,
            micOn,
            waitingRoom,
            requirePasscode: passcode,
            liveTranscription
        });
    };
    const handleCopyLink = async () => {
        const link = `${window.location.origin}/${generatedRoomName}`;
        await copyText(link);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };
    const handleSettingsClick = () => {
        dispatch(openSettingsDialog(SETTINGS_TABS.AUDIO));
    };
    const handleBgEffectsClick = () => {
        dispatch(openSettingsDialog(SETTINGS_TABS.VIRTUAL_BACKGROUND));
    };
    return (React.createElement("div", { className: 'fc-host-meeting-wrapper' },
        React.createElement("div", { className: 'fc-hm-left' },
            React.createElement("div", { className: 'fc-hm-video-container' },
                React.createElement("div", { className: 'fc-hm-bg-effects-btn', onClick: handleBgEffectsClick }, "\u26F6 Background effects"),
                React.createElement("div", { className: 'fc-hm-video-preview' },
                    React.createElement(Preview, { videoMuted: !videoOn, videoTrack: videoTrack })),
                React.createElement("div", { className: 'fc-hm-controls' },
                    React.createElement("div", { className: 'fc-hm-control-item' },
                        React.createElement("button", { className: 'fc-hm-icon-btn', onClick: () => setMicOn(!micOn) },
                            React.createElement(Mic, { size: 24, color: micOn ? "white" : "red" }),
                            React.createElement("div", { className: "fc-hm-chevron" },
                                React.createElement(ChevronDown, { size: 12, color: "white" }))),
                        React.createElement("span", { className: 'fc-hm-control-label' }, "Microphone"),
                        React.createElement("span", { className: 'fc-hm-control-sublabel' }, "Default")),
                    React.createElement("div", { className: 'fc-hm-control-item' },
                        React.createElement("button", { className: 'fc-hm-icon-btn', onClick: () => setVideoOn(!videoOn) },
                            React.createElement(Video, { size: 24, color: videoOn ? "white" : "red" }),
                            React.createElement("div", { className: "fc-hm-chevron" },
                                React.createElement(ChevronDown, { size: 12, color: "white" }))),
                        React.createElement("span", { className: 'fc-hm-control-label' }, "Camera"),
                        React.createElement("span", { className: 'fc-hm-control-sublabel' }, "FaceTime HD")),
                    React.createElement("div", { className: 'fc-hm-control-item' },
                        React.createElement("button", { className: 'fc-hm-icon-btn', onClick: handleBgEffectsClick },
                            React.createElement("div", { style: { width: 24, height: 24, border: '2px solid white', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                                React.createElement("div", { style: { width: 12, height: 12, backgroundColor: 'white', borderRadius: 2 } }))),
                        React.createElement("span", { className: 'fc-hm-control-label' }, "Background"),
                        React.createElement("span", { className: 'fc-hm-control-sublabel' }, "None")),
                    React.createElement("div", { className: 'fc-hm-control-item' },
                        React.createElement("button", { className: 'fc-hm-icon-btn', onClick: handleSettingsClick },
                            React.createElement(Settings, { size: 24, color: "white" })),
                        React.createElement("span", { className: 'fc-hm-control-label' }, "Settings"))))),
        React.createElement("div", { className: 'fc-hm-right' },
            React.createElement("h1", { className: 'fc-hm-title' }, "Host a meeting"),
            React.createElement("p", { className: 'fc-hm-subtitle' }, "Start an instant meeting and invite others to join."),
            React.createElement("div", { className: 'fc-hm-form' },
                React.createElement("label", { className: 'fc-hm-label' }, "Your name"),
                React.createElement("input", { className: 'fc-hm-input', onChange: e => setName(e.target.value), type: 'text', value: name }),
                React.createElement("h3", { className: 'fc-hm-section-title' }, "Meeting options"),
                React.createElement("div", { className: 'fc-hm-toggles' },
                    React.createElement("label", { className: 'fc-hm-toggle-row' },
                        React.createElement("div", { className: "fc-hm-toggle-label" },
                            React.createElement(Video, { size: 20, color: "#6b7280" }),
                            React.createElement("span", null, "Start with video on")),
                        React.createElement("div", { className: `fc-hm-switch ${videoOn ? 'active' : ''}`, onClick: () => setVideoOn(!videoOn) },
                            React.createElement("div", { className: "fc-hm-switch-knob" }))),
                    React.createElement("label", { className: 'fc-hm-toggle-row' },
                        React.createElement("div", { className: "fc-hm-toggle-label" },
                            React.createElement(Mic, { size: 20, color: "#6b7280" }),
                            React.createElement("span", null, "Start with microphone on")),
                        React.createElement("div", { className: `fc-hm-switch ${micOn ? 'active' : ''}`, onClick: () => setMicOn(!micOn) },
                            React.createElement("div", { className: "fc-hm-switch-knob" }))),
                    React.createElement("label", { className: 'fc-hm-toggle-row' },
                        React.createElement("div", { className: "fc-hm-toggle-label" },
                            React.createElement(User, { size: 20, color: "#6b7280" }),
                            React.createElement("span", null, "Enable waiting room")),
                        React.createElement("div", { className: `fc-hm-switch ${waitingRoom ? 'active' : ''}`, onClick: () => setWaitingRoom(!waitingRoom) },
                            React.createElement("div", { className: "fc-hm-switch-knob" }))),
                    React.createElement("label", { className: 'fc-hm-toggle-row' },
                        React.createElement("div", { className: "fc-hm-toggle-label" },
                            React.createElement(Shield, { size: 20, color: "#6b7280" }),
                            React.createElement("span", null, "Require passcode")),
                        React.createElement("div", { className: `fc-hm-switch ${requirePasscode ? 'active' : ''}`, onClick: () => setRequirePasscode(!requirePasscode) },
                            React.createElement("div", { className: "fc-hm-switch-knob" }))),
                    React.createElement("label", { className: 'fc-hm-toggle-row' },
                        React.createElement("div", { className: "fc-hm-toggle-label" },
                            React.createElement(MessageSquare, { size: 20, color: "#6b7280" }),
                            React.createElement("span", null, "Enable live transcription (captions)")),
                        React.createElement("div", { className: `fc-hm-switch ${liveTranscription ? 'active' : ''}`, onClick: () => setLiveTranscription(!liveTranscription) },
                            React.createElement("div", { className: "fc-hm-switch-knob" })))),
                React.createElement("div", { className: 'fc-hm-advanced' },
                    React.createElement("span", null, "Advanced options"),
                    React.createElement(ChevronDown, { size: 16 })),
                React.createElement("button", { className: 'fc-hm-btn-start', onClick: handleHost }, "Start meeting"),
                React.createElement("button", { className: 'fc-hm-btn-copy', onClick: handleCopyLink },
                    React.createElement(Copy, { size: 18 }),
                    isCopied ? 'Copied!' : 'Copy invitation link')))));
}
